import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {SearchRule} from 'constants/SequenceOpConstants';
import FolderSelector from 'file-browser/FolderSelector';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {NucleotideType, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SequenceMapPreviewJustify, BulkEditModsResult} from 'modbio/bulk-edit-mods/BulkEditModsConstants';
import BulkEditModsPreviewsListing from 'modbio/bulk-edit-mods/BulkEditModsPreviewsListing';
import {getShouldCloneLabelText} from 'modbio/bulk-edit-mods/BulkEditModsUtil';
import {
  bulkApplyFindAndReplace,
  bulkPreviewFindAndReplace,
  BulkApplyFindAndReplaceParams,
} from 'modbio/bulk-edit-mods/NucleotidesFindAndReplaceDAO';
import {
  FindAndReplaceModalSequenceQuery as SequencesData,
  FindAndReplaceModalSequenceQuery,
} from 'molbio/monomers/__generated/FindAndReplaceModalSequenceQuery';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE_LIGHT, GRAY_DARKER, RED_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';
import {omit} from 'util/omitKey';
import {InputChangeEvent} from 'util/Types';

import ModifiedNucleotidesLegend from './ModifiedNucleotidesLegend';
import {MissingMonomerReason, NucleotideComponent} from './MonomerConstants';
import {SelfOpeningMonomerDropdown, SpecialMonomerValue} from './MonomerDropdown';

type SequenceGraphQL = ExtractByTypename<
  FindAndReplaceModalSequenceQuery['sequences'][number],
  'SequenceNode'
>;

interface RuleDropdownProps {
  hasError: boolean;
  nucleotideType: NucleotideType;
  ruleState: SearchRule;
  setRuleState: (state: SearchRule) => void;
  path: ['target' | 'replacement', 'sugarMonomerId' | 'baseMonomerId' | 'phosphateMonomerId'];
  disabled: boolean;
}
function RuleDropdown({
  hasError,
  nucleotideType,
  ruleState,
  setRuleState,
  path,
  disabled,
}: RuleDropdownProps): JSX.Element {
  const allowedSpecialValues =
    path[0] === 'target'
      ? [SpecialMonomerValue.ANY_MONOMER, SpecialMonomerValue.ANY_NATURAL_MONOMER]
      : [SpecialMonomerValue.EXISTING_MONOMER];

  const nucleotideComponent = path[1].startsWith('sugar')
    ? NucleotideComponent.SUGAR
    : path[1].startsWith('base')
      ? NucleotideComponent.BASE
      : NucleotideComponent.PHOSPHATE;
  return (
    <SelfOpeningMonomerDropdown
      nameMaxLength={25}
      fullWidth={true}
      allowArchived={path[0] === 'target'}
      buttonClassName={hasError ? css(styles.invalidMonomer) : ''}
      nucleotideType={nucleotideType}
      selectedMonomerId={ruleState[path[0]][path[1]] ?? MissingMonomerReason.NULL_MONOMER}
      nucleotideComponent={nucleotideComponent}
      allowedSpecialValues={allowedSpecialValues}
      includeNullMonomer={path[0] === 'target' && path[1].startsWith('phosphate')}
      updateSelectedMonomerId={(newId) => {
        setRuleState({
          ...ruleState,
          [path[0]]: {
            ...ruleState[path[0]],
            [path[1]]: newId,
          },
        });
      }}
      disabled={disabled}
    />
  );
}

interface RuleProps {
  remove: () => void;
  nucleotideType: NucleotideType;
  ruleState: SearchRule;
  setRuleState: (state: SearchRule) => void;
  hasError: boolean;
  disabled: boolean;
}
function Rule(props: RuleProps): JSX.Element {
  const {ruleState, nucleotideType, setRuleState, hasError, remove, disabled} = props;
  return (
    <>
      <tr>
        <td className={css(styles.textCell)}>Find nucleotides with</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'findNucleotidesDropdown',
            key: 'sugar',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['target', 'sugarMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)}>and</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'findNucleotidesDropdown',
            key: 'base',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['target', 'baseMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)}>and</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'findNucleotidesDropdown',
            key: 'phosphate',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['target', 'phosphateMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)}>
          {!disabled && <BenchlingIcon icon="close" className={css(styles.removeIcon)} onClick={remove} />}
        </td>
      </tr>

      <tr>
        <td className={css(styles.textCell)}>Replace with</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'replaceWithDropdown',
            key: 'sugar',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['replacement', 'sugarMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)}>and</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'replaceWithDropdown',
            key: 'base',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['replacement', 'baseMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)}>and</td>
        <td
          className={css(styles.dropdownCell)}
          {...getTestProps({
            component: 'NucleotidesFindAndReplaceModal',
            element: 'replaceWithDropdown',
            key: 'phosphate',
          })}
        >
          <RuleDropdown
            {...{
              hasError,
              nucleotideType,
              ruleState,
              setRuleState,
              disabled,
              path: ['replacement', 'phosphateMonomerId'],
            }}
          />
        </td>
        <td className={css(styles.textCell)} />
      </tr>

      <tr className={css(styles.spacerRow)} />
    </>
  );
}

const DEFAULT_RULE_STATE = {
  target: {
    sugarMonomerId: SpecialMonomerValue.ANY_MONOMER,
    baseMonomerId: SpecialMonomerValue.ANY_MONOMER,
    phosphateMonomerId: SpecialMonomerValue.ANY_MONOMER,
  },
  replacement: {
    sugarMonomerId: SpecialMonomerValue.EXISTING_MONOMER,
    baseMonomerId: SpecialMonomerValue.EXISTING_MONOMER,
    phosphateMonomerId: SpecialMonomerValue.EXISTING_MONOMER,
  },
};

interface RulesState {
  [index: number]: SearchRule;
}

export const FIND_AND_REPLACE_MODAL_SEQUENCE_QUERY = gql`
  query FindAndReplaceModalSequenceQuery($ids: [StrictString]!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    sequences(ids: $ids) {
      ... on SequenceNode {
        id
        latestVersionId
        name
        length
        sequenceType
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function queryDataToSequences({sequences: sequencesOrInaccessible}: SequencesData): {
  sequenceNames: Array<string>;
  sequenceVersionIds: Array<number>;
  sequenceTypes: Array<SequenceType>;
} {
  const seqs = sequencesOrInaccessible.filter(
    (sequenceOrInaccessible): sequenceOrInaccessible is SequenceGraphQL =>
      !isInaccessible(sequenceOrInaccessible)
  );

  const sequenceNames = seqs.map((seq) => seq.name);

  const sequenceVersionIds = seqs.map((seq) => {
    assert(seq.latestVersionId != null, 'Expected selected oligo to have non-null version id.');

    return seq.latestVersionId;
  });

  const sequenceTypes = seqs.map((seq) => seq.sequenceType);

  return {
    sequenceNames,
    sequenceVersionIds,
    sequenceTypes,
  };
}

const TEMPLATE_LENGTH_FIND_AND_REPLACE = 0;

export interface BasesAndModifiedNucleotides {
  bases: string;
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>;
}

export interface NucleotidesFindAndReplaceModalProps extends QueryNucleotidesFindAndReplaceModalProps {
  sequenceNames: Array<string>;
  sequenceVersionIds: Array<number>;
  sequenceTypes: Array<SequenceType>;
}

export function NucleotidesFindAndReplaceModal(props: NucleotidesFindAndReplaceModalProps): JSX.Element {
  const [rulesState, setRulesState] = useState<RulesState>({0: DEFAULT_RULE_STATE});
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [conflicts, setConflicts] = useState<Array<number>>([]);
  const [results, setResults] = useState<Array<BulkEditModsResult>>([]);
  const [submissionMessage, setSubmissionMessage] = useState<InlineBannerProps | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isCloneChecked, setIsCloneChecked] = useState(false);
  const [folderId, setFolderId] = useState(FolderDefaults.get(FolderDefaultsKey.OLIGO));

  useEffect(() => {
    const validate = async (): Promise<void> => {
      setFetchingPreview(true);
      const {conflicts, results} = await bulkPreviewFindAndReplace(
        props.sequenceVersionIds,
        Object.values(rulesState)
      );
      setConflicts(conflicts);
      setResults(results);
      setFetchingPreview(false);
      if (conflicts.length > 0) {
        setSubmissionMessage({
          bannerType: InlineBannerType.ERROR,
          text: 'The rules above are in conflict with each other. The conflicts are highlighted in red.',
        });
      } else {
        setSubmissionMessage(null);
      }
    };
    validate();
  }, [rulesState, props.sequenceVersionIds]);

  function isRNASequence(): boolean {
    return props.sequenceTypes[0] === SequenceType.ss;
  }

  function getSuccessfulResults(): Array<BulkEditModsResult> {
    return results.filter((res) => res.preview != null);
  }

  function getAggregatePreviewModsForLegend(): BasesAndModifiedNucleotides | null {
    const successfulResults = getSuccessfulResults();
    if (successfulResults.length === 0) {
      return null;
    }
    const allBases = successfulResults.reduce((currBases, result) => currBases + result.preview!.bases, '');
    const allMods = successfulResults.flatMap((result) => result.preview!.modifiedNucleotides);
    return {
      bases: allBases,
      modifiedNucleotides: allMods,
    };
  }

  function getPreviewDisplay(): JSX.Element {
    const sequenceTypes = new Set(props.sequenceTypes);
    assert(
      props.sequenceIds.length > 0,
      'Must have at least one item selected to apply find-and-replace rules'
    );
    assert(
      sequenceTypes.size === 1,
      'Find-and-replace rules can only be applied to items of the same type (i.e. all RNA sequences or all oligos)'
    );
    assert(
      props.sequenceTypes[0] !== SequenceType.ds,
      'Find-and-replace rules can only be applied to oligos or RNA sequences'
    );
    const tableStyles = [
      styles.table,
      props.sequenceIds.length > 1 ? styles.multipleRowTable : styles.singleRowTable,
    ];

    return (
      <div>
        {getSuccessfulResults().length > 0 &&
          getAggregatePreviewModsForLegend()!.modifiedNucleotides.length > 0 &&
          !isRNASequence() && (
            <div className={css(styles.legend)}>
              <ModifiedNucleotidesLegend
                sequenceId={props.sequenceIds[0]}
                useNetworkQuery={true}
                axisHighlightComponentTitle="Nucleotide altered by find and replace"
                showBaseHighlight={false}
                displayModsOverride={getAggregatePreviewModsForLegend()}
              />
            </div>
          )}
        <div className={css(tableStyles)}>
          <BulkEditModsPreviewsListing
            templateBasesLength={TEMPLATE_LENGTH_FIND_AND_REPLACE}
            targetOligoNames={props.sequenceNames}
            results={results}
            justify={SequenceMapPreviewJustify.LEFT}
            nucleotideType={props.nucleotideType}
            submitted={submitted}
            customStartPosition={null}
            emptyTableText="No sequence changes to preview"
            sequenceType={props.sequenceTypes[0]}
            successfulResultTooltipText={`Find-and-replace rules successfully applied to ${
              isRNASequence() ? 'sequence' : 'oligo'
            }`}
            previewColumnTitle="Modifications"
          />
        </div>
      </div>
    );
  }

  async function applyFindAndReplace(): Promise<void> {
    try {
      LoadingScreen.show('Applying find and replace rules...');
      setSubmitted(true);

      let bulkApplyFindAndReplaceParams: BulkApplyFindAndReplaceParams;
      const sharedParams = {
        sequenceVersionIds: props.sequenceVersionIds,
        rules: Object.values(rulesState),
        isBulk: props.isBulk,
      };
      if (isCloneChecked) {
        assert(folderId != null, 'Folder must be selected when cloning');
        bulkApplyFindAndReplaceParams = {
          ...sharedParams,
          shouldClone: true,
          folderId,
        };
      } else {
        bulkApplyFindAndReplaceParams = {
          ...sharedParams,
          shouldClone: false,
          folderId: null,
        };
      }

      const {conflicts, results} = await bulkApplyFindAndReplace(bulkApplyFindAndReplaceParams);

      setConflicts(conflicts);
      setResults(results);

      await FileStates.get().updateSequenceVersions(props.sequenceIds);
    } catch (e) {
      setResults([]);
      setSubmissionMessage({
        bannerType: InlineBannerType.ERROR,
        text: 'Something went wrong while applying the find-and-replace rules. Please retry the operation with the same settings.',
      });
      LoadingScreen.hide();
      throw e;
    }

    const numSuccess = results.filter((result) => result.error == null).length;
    const numFail = results.length - numSuccess;

    if (numFail === 0) {
      const entityDescriptor = pluralize(props.sequenceIds.length, isRNASequence() ? 'sequence' : 'oligo');
      const successMessage = isCloneChecked
        ? `Created and successfully applied find-and-replace rules to ${props.sequenceIds.length} ${entityDescriptor}`
        : `Find-and-replace rules applied successfully to ${entityDescriptor}`;
      if (props.shouldSkipResultsScreenAfterSuccess) {
        notifications.success(successMessage);
        LegacyModalContainer.close(QueryNucleotidesFindAndReplaceModal);
      } else {
        setSubmissionMessage({
          bannerType: InlineBannerType.SUCCESS,
          text: `${successMessage}. Please review the results below.`,
        });
      }
    } else {
      setSubmissionMessage({
        bannerType: InlineBannerType.WARNING,
        text: `Find-and-replace rules were successfully applied to ${numSuccess} ${pluralize(
          numSuccess,
          isRNASequence() ? 'sequence' : 'oligo'
        )}, and could not be applied to ${numFail} ${pluralize(
          numFail,
          isRNASequence() ? 'sequence' : 'oligo'
        )}. Please review the results below.`,
      });
    }
    LoadingScreen.hide();
  }

  return (
    <Modal
      title="Find and replace"
      large={true}
      buttons={[
        {text: submitted ? 'Close' : 'Cancel', cancel: true},
        {
          text: isCloneChecked ? 'Create' : 'Replace',
          submit: true,
          disabled:
            submitted ||
            fetchingPreview ||
            conflicts.length > 0 ||
            getSuccessfulResults().length === 0 ||
            (isCloneChecked && folderId == null),
        },
      ]}
      onRequestClose={() => {
        LegacyModalContainer.close(QueryNucleotidesFindAndReplaceModal);
      }}
      onRequestSubmit={applyFindAndReplace}
    >
      <>
        <p className="u-spacing-bottom">
          Replace nucleotides with a specific combination of monomers. These changes will apply to all
          relevant nucleotides across the whole sequence.
        </p>
        <div>
          <h4 className="u-gray-darker">
            Rule(s)
            {!submitted && (
              <BenchlingIcon
                icon="plus"
                className={css(styles.addIcon)}
                onClick={() => {
                  const indices = Object.keys(rulesState).map((k) => Number(k));
                  const newIndex = indices.length > 0 ? Math.max(...indices) + 1 : 0;
                  setRulesState({
                    ...rulesState,
                    [newIndex]: DEFAULT_RULE_STATE,
                  });
                }}
              />
            )}
          </h4>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th />
              <th className={css(styles.headerCell)}>Sugar</th>
              <th />
              <th className={css(styles.headerCell)}>Base</th>
              <th />
              <th className={css(styles.headerCell)}>Phosphate</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {Object.entries(rulesState).map(([index, ruleState]) => (
              <Rule
                key={index}
                hasError={conflicts.includes(Number(index))}
                ruleState={ruleState}
                nucleotideType={props.nucleotideType}
                setRuleState={(newState) => {
                  setRulesState({...rulesState, [index]: newState});
                }}
                remove={() => {
                  setRulesState(omit(rulesState, Number(index)));
                }}
                disabled={submitted}
              />
            ))}
          </tbody>
        </table>
        <div>
          {submissionMessage && (
            <InlineBanner
              {...submissionMessage}
              dataTestAttrs={{
                component: 'NucleotidesFindAndReplaceModal',
                element: 'banner',
              }}
            />
          )}
        </div>
        <div>
          <h4 className="u-gray-darker">Preview changes</h4>
          {fetchingPreview ? <Spinner /> : getPreviewDisplay()}
        </div>
        <CheckboxInput
          className={css(styles.checkbox)}
          id="find-and-replace-clone"
          label={getShouldCloneLabelText(props.isBulk, getSuccessfulResults().length)}
          checked={isCloneChecked}
          onChange={(e: InputChangeEvent) => {
            setIsCloneChecked(e.target.checked);
          }}
          disabled={submitted || getSuccessfulResults().length === 0}
        />
        {isCloneChecked && (
          <div className={css(styles.folderSelector)}>
            <FieldLabel label={`Folder for new ${pluralize(getSuccessfulResults().length, 'entity')}`} />
            <FolderSelector
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e: {target: {value: string}}) => {
                setFolderId(e.target.value);
              }}
            />
          </div>
        )}
      </>
    </Modal>
  );
}

export interface QueryNucleotidesFindAndReplaceModalProps {
  sequenceIds: Array<string>;
  nucleotideType: NucleotideType;
  shouldSkipResultsScreenAfterSuccess: boolean;
  isBulk: boolean;
}
export default function QueryNucleotidesFindAndReplaceModal(
  props: QueryNucleotidesFindAndReplaceModalProps
): JSX.Element {
  return (
    <BenchlingQuery<FindAndReplaceModalSequenceQuery>
      query={FIND_AND_REPLACE_MODAL_SEQUENCE_QUERY}
      variables={{ids: props.sequenceIds}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      fetchPolicy="network-only"
    >
      {(data) => {
        const {sequenceNames, sequenceVersionIds, sequenceTypes} = queryDataToSequences(data);
        return (
          <NucleotidesFindAndReplaceModal
            sequenceIds={props.sequenceIds}
            nucleotideType={props.nucleotideType}
            sequenceNames={sequenceNames}
            sequenceVersionIds={sequenceVersionIds}
            sequenceTypes={sequenceTypes}
            shouldSkipResultsScreenAfterSuccess={props.shouldSkipResultsScreenAfterSuccess}
            isBulk={props.isBulk}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  invalidMonomer: {
    border: `1px solid ${RED_LIGHT}`,
  },
  headerCell: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: GRAY_DARKER,
    paddingBottom: '8px',
  },
  textCell: {
    verticalAlign: 'middle',
    textAlign: 'right',
    fontWeight: 'bold',
    color: GRAY_DARKER,
    border: 0,
  },
  dropdownCell: {
    border: 0,
    width: '24%',
  },
  addIcon: {
    cursor: 'pointer',
    color: BLUE_LIGHT,
    marginLeft: '5px',
  },
  removeIcon: {
    cursor: 'pointer',
  },
  spacerRow: {
    height: '10px',
  },
  table: {
    display: 'flex',
    flex: 1,
  },
  singleRowTable: {
    minHeight: '200px',
  },
  multipleRowTable: {
    minHeight: '350px',
  },
  legend: {
    display: 'flex',
    marginLeft: -20,
    marginTop: -16,
    width: '100%',
  },
  checkbox: {
    paddingTop: 8,
  },
  folderSelector: {
    paddingTop: 8,
    width: '30%',
  },
});
